import React, { useCallback, useEffect, useMemo, useState, Suspense } from 'react';
import styled from 'styled-components';
import { Spinner } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import PeriodSelectBox from '../../global/PeriodSelectBox';
import { fetchPerformanceInsights } from '../../../services/Http/Api';
import { useCurrency } from '../../../context/CurrencyProvider';

const ReactApexChart = React.lazy(() => import('react-apexcharts'));

const defaultSummary = {
    total_units_sold: 0,
    total_revenue: 0,
    average_selling_price: 0,
    stock_on_hand: 0,
    at_risk_skus: 0,
    avg_days_on_shelf: 0
};

const formatNumber = (value) => Number(value || 0).toLocaleString();
const formatDecimal = (value) => Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 1 });

const PerformanceInsights = () => {
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const [period, setPeriod] = useState('last_7_days');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [insights, setInsights] = useState({
        summary: defaultSummary,
        topSelling: [],
        slowMoving: [],
        brandMix: [],
        salesTrend: []
    });

    const loadInsights = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchPerformanceInsights({ period });
            setInsights({
                summary: data.summary || defaultSummary,
                topSelling: data.top_selling_items || [],
                slowMoving: data.slow_moving_items || [],
                brandMix: data.brand_mix || [],
                salesTrend: data.sales_trend || []
            });
        } catch (err) {
            setError(err?.message || 'Unable to load performance insights');
        } finally {
            setLoading(false);
        }
    }, [period]);

    useEffect(() => {
        loadInsights();
    }, [loadInsights]);

    const brandMixChart = useMemo(() => {
        if (!insights.brandMix?.length) {
            return null;
        }

        return {
            options: {
                labels: insights.brandMix.map((entry) => entry.manufacturer_name || t('common.none')),
                legend: {
                    position: 'bottom'
                },
                dataLabels: {
                    formatter: (val) => `${val.toFixed(1)}%`
                }
            },
            series: insights.brandMix.map((entry) => Number(entry.revenue || 0))
        };
    }, [insights.brandMix, t]);

    const trendChart = useMemo(() => {
        if (!insights.salesTrend?.length) {
            return null;
        }

        return {
            options: {
                chart: {
                    id: 'retailer-sales-trend',
                    toolbar: {
                        show: false
                    }
                },
                stroke: {
                    curve: 'smooth'
                },
                xaxis: {
                    categories: insights.salesTrend.map((entry) => entry.label)
                },
                tooltip: {
                    y: {
                        formatter: (value) => value.toLocaleString()
                    }
                }
            },
            series: [
                {
                    name: t('Items.Performance.charts.unitsSeries'),
                    data: insights.salesTrend.map((entry) => entry.units || 0)
                }
            ]
        };
    }, [insights.salesTrend, t]);

    const renderTableRows = (items) => {
        if (!items?.length) {
            return (
                <tr>
                    <td colSpan="6">
                        <EmptyState>{t('Items.Performance.emptyState')}</EmptyState>
                    </td>
                </tr>
            );
        }

        return items.map((item) => (
            <tr key={item.manufacturer_item_id}>
                <td>
                    <strong>{item.product_name}</strong>
                    <small>{item.model_number}</small>
                </td>
                <td>{item.manufacturer_name || t('common.none')}</td>
                <td>{formatNumber(item.units_sold)}</td>
                <td>{formatResolvedAmount({
                    eurAmount: item.revenue,
                    localAmount: item.revenue_local ?? item.revenue_seller_local,
                    localCurrencyId: item.currency_id ?? item.seller_currency_id,
                    options: { minimumFractionDigits: 0, maximumFractionDigits: 0 },
                })}</td>
                <td>{formatNumber(item.stock)}</td>
                <td>{item.last_sale_at ? new Date(item.last_sale_at).toLocaleDateString() : t('common.none')}</td>
            </tr>
        ));
    };

    return (
        <Wrapper>
            <HeaderRow>
                <div>
                    <SectionTitle>{t('Items.Performance.title')}</SectionTitle>
                    <SectionSubtitle>{t('Items.Performance.subtitle')}</SectionSubtitle>
                </div>
                <PeriodSelectBox value={period} onChange={(event) => setPeriod(event.target.value)} />
            </HeaderRow>

            {error && <ErrorBanner>{error}</ErrorBanner>}

            {loading ? (
                <LoaderWrapper>
                    <Spinner color="primary" />
                </LoaderWrapper>
            ) : (
                <>
                    <SummaryGrid>
                        <SummaryCard>
                            <label>{t('Items.Performance.summary.totalUnits')}</label>
                            <strong>{formatNumber(insights.summary.total_units_sold)}</strong>
                        </SummaryCard>
                        <SummaryCard>
                            <label>{t('Items.Performance.summary.totalRevenue')}</label>
                            <strong>{formatResolvedAmount({
                                eurAmount: insights.summary.total_revenue,
                                localAmount: insights.summary.total_revenue_local,
                                localCurrencyId: insights.summary.currency_id,
                                options: { minimumFractionDigits: 0, maximumFractionDigits: 0 },
                            })}</strong>
                        </SummaryCard>
                        <SummaryCard>
                            <label>{t('Items.Performance.summary.avgSellingPrice')}</label>
                            <strong>{formatResolvedAmount({
                                eurAmount: insights.summary.average_selling_price,
                                localAmount: insights.summary.average_selling_price_local,
                                localCurrencyId: insights.summary.currency_id,
                                options: { minimumFractionDigits: 0, maximumFractionDigits: 0 },
                            })}</strong>
                        </SummaryCard>
                        <SummaryCard>
                            <label>{t('Items.Performance.summary.stockOnHand')}</label>
                            <strong>{formatNumber(insights.summary.stock_on_hand)}</strong>
                        </SummaryCard>
                        <SummaryCard>
                            <label>{t('Items.Performance.summary.atRiskSkus')}</label>
                            <strong>{formatNumber(insights.summary.at_risk_skus)}</strong>
                        </SummaryCard>
                        <SummaryCard>
                            <label>{t('Items.Performance.summary.avgDaysOnShelf')}</label>
                            <strong>{formatDecimal(insights.summary.avg_days_on_shelf)}</strong>
                        </SummaryCard>
                    </SummaryGrid>

                    <ChartsGrid>
                        <SectionCard>
                            <CardTitle>{t('Items.Performance.charts.brandMix')}</CardTitle>
                            {brandMixChart ? (
                                <Suspense fallback={<Spinner color="primary" />}>
                                    <ReactApexChart
                                        type="donut"
                                        options={brandMixChart.options}
                                        series={brandMixChart.series}
                                        height={300}
                                    />
                                </Suspense>
                            ) : (
                                <EmptyState>{t('Items.Performance.emptyState')}</EmptyState>
                            )}
                        </SectionCard>

                        <SectionCard>
                            <CardTitle>{t('Items.Performance.charts.salesTrend')}</CardTitle>
                            {trendChart ? (
                                <Suspense fallback={<Spinner color="primary" />}>
                                    <ReactApexChart
                                        type="line"
                                        options={trendChart.options}
                                        series={trendChart.series}
                                        height={300}
                                    />
                                </Suspense>
                            ) : (
                                <EmptyState>{t('Items.Performance.emptyState')}</EmptyState>
                            )}
                        </SectionCard>
                    </ChartsGrid>

                    <TablesGrid>
                        <SectionCard>
                            <CardTitle>{t('Items.Performance.tables.topSellers')}</CardTitle>
                            <PerformanceTable>
                                <thead>
                                    <tr>
                                        <th>{t('Items.Performance.tables.columns.item')}</th>
                                        <th>{t('Items.Performance.tables.columns.manufacturer')}</th>
                                        <th>{t('Items.Performance.tables.columns.units')}</th>
                                        <th>{t('Items.Performance.tables.columns.revenue')}</th>
                                        <th>{t('Items.Performance.tables.columns.stock')}</th>
                                        <th>{t('Items.Performance.tables.columns.lastSold')}</th>
                                    </tr>
                                </thead>
                                <tbody>{renderTableRows(insights.topSelling)}</tbody>
                            </PerformanceTable>
                        </SectionCard>

                        <SectionCard>
                            <CardTitle>{t('Items.Performance.tables.slowMovers')}</CardTitle>
                            <PerformanceTable>
                                <thead>
                                    <tr>
                                        <th>{t('Items.Performance.tables.columns.item')}</th>
                                        <th>{t('Items.Performance.tables.columns.manufacturer')}</th>
                                        <th>{t('Items.Performance.tables.columns.units')}</th>
                                        <th>{t('Items.Performance.tables.columns.revenue')}</th>
                                        <th>{t('Items.Performance.tables.columns.stock')}</th>
                                        <th>{t('Items.Performance.tables.columns.lastSold')}</th>
                                    </tr>
                                </thead>
                                <tbody>{renderTableRows(insights.slowMoving)}</tbody>
                            </PerformanceTable>
                        </SectionCard>
                    </TablesGrid>
                </>
            )}
        </Wrapper>
    );
};

export default PerformanceInsights;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
`;

const HeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.75rem;
`;

const SectionTitle = styled.h3`
    margin: 0;
    color: rgba(130, 22, 146, 1);
`;

const SectionSubtitle = styled.p`
    margin: 0;
    color: ${({ theme }) => theme.colors.textSecondary || '#666'};
`;

const SummaryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 1rem;
`;

const SummaryCard = styled.div`
    border: 1px solid rgba(130, 22, 146, 0.12);
    border-radius: 10px;
    padding: 1rem;
    background: rgba(130, 22, 146, 0.02);
    label {
        display: block;
        color: ${({ theme }) => theme.colors.textSecondary || '#666'};
        font-size: 0.875rem;
    }
    strong {
        display: block;
        font-size: 1.4rem;
        color: rgba(130, 22, 146, 1);
        margin-top: 0.25rem;
    }
`;

const ChartsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1rem;
`;

const TablesGrid = styled(ChartsGrid)`
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
`;

const SectionCard = styled.div`
    border: 1px solid rgba(130, 22, 146, 0.15);
    border-radius: 12px;
    padding: 1.25rem;
    background: #fff;
`;

const CardTitle = styled.h5`
    margin: 0 0 0.75rem 0;
    color: rgba(130, 22, 146, 1);
`;

const PerformanceTable = styled.table`
    width: 100%;
    border-collapse: collapse;

    th, td {
        text-align: left;
        padding: 0.625rem 0.5rem;
        border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    }

    th {
        font-size: 0.875rem;
        color: ${({ theme }) => theme.colors.textSecondary || '#666'};
        text-transform: uppercase;
    }

    td small {
        display: block;
        color: ${({ theme }) => theme.colors.textSecondary || '#666'};
    }
`;

const EmptyState = styled.div`
    text-align: center;
    color: ${({ theme }) => theme.colors.textSecondary || '#666'};
    padding: 1.25rem 0;
`;

const LoaderWrapper = styled.div`
    display: flex;
    justify-content: center;
    padding: 2.5rem 0;
`;

const ErrorBanner = styled.div`
    background: rgba(220, 53, 69, 0.1);
    color: #721c24;
    border: 1px solid rgba(220, 53, 69, 0.3);
    padding: 0.75rem 1rem;
    border-radius: 8px;
`;
